import ExpanderList from '@rio-cloud/rio-uikit/ExpanderList';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import LabeledValue from '#components/form/LabeledValue.tsx';
import type { Documents } from '#domain/Document.ts';
import type { StopDetails } from '#domain/Stop.ts';
import type { Tour } from '#domain/Tour.ts';

import { getTourExpanderItems } from './TourExpanderItems.tsx';
import { TourStopProgress } from './TourStopProgress.tsx';
import { TourStopProgressHeader } from './TourStopProgressHeader.tsx';
import { TourStopContextProvider } from './tourStop.context.tsx';

type TourDetailsSidebarProps = {
    tour: Tour;
    selectedTourStops: StopDetails[];
    tourDocuments?: Documents;
    setSelectedStop: (stop?: StopDetails) => void;
    setSelectedDocumentStop: (stop?: StopDetails) => void;
    vehicleName?: string;
    showDocuments?: boolean;
};

export const TourDetails = ({
    tour,
    selectedTourStops,
    tourDocuments,
    setSelectedStop,
    setSelectedDocumentStop,
    vehicleName,
    showDocuments = true,
}: TourDetailsSidebarProps) => {
    const [selectedItemIndex, setSelectedItemIndex] = useState<number | undefined>(0);
    const intl = useIntl();

    const expanderItems = getTourExpanderItems({
        tour: tour,
        documents: tourDocuments,
        selectedItemIndex,
        setSelectedItemIndex,
        setSelectedStop,
        setSelectedDocumentStop,
        showDocuments,
    });

    return (
        <div data-testid='tour-details'>
            <TourStopContextProvider selectedTour={tour} selectedTourStops={selectedTourStops}>
                <div className='padding-x-15 padding-bottom-15'>
                    <div className='display-flex align-items-start gap-50'>
                        <TourStopProgressHeader tour={tour} />
                        {tour.status === 'in-transit' && vehicleName && (
                            <div
                                className='display-flex flex-column align-items-start gap-3 flex-shrink-0'
                                data-testid='tour-details-vehicle-info'
                            >
                                <div className='text-medium line-height-125rel'>
                                    {intl.formatMessage({
                                        id: 'intl-msg:ordercom-cloud-frontend.tours.table.header.vehicle',

                                        defaultMessage: 'Vehicle',
                                    })}
                                    :
                                </div>
                                <span className='text-size-11 text-medium'>{vehicleName}</span>
                            </div>
                        )}
                    </div>
                    {tour.status !== 'planned' && <TourStopProgress stops={selectedTourStops} />}
                </div>
                {tour.note && (
                    <div className='padding-x-15 padding-bottom-15'>
                        <LabeledValue
                            label={intl.formatMessage({
                                id: 'intl-msg:ordercom-cloud-frontend.tours.note',
                                defaultMessage: 'Tour comment',
                            })}
                            className='padding-x-15 padding-y-5'
                            icon='comment'
                            value={
                                <span style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{tour.note}</span>
                            }
                        />
                    </div>
                )}
                <ExpanderList
                    className='border border-left-none border-right-none'
                    rounded={false}
                    items={expanderItems}
                />
            </TourStopContextProvider>
        </div>
    );
};
